import { get, flowRight as compose } from 'lodash';
import getConfig from 'next/config';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { injectIntl } from 'react-intl';
import intlShape from '@lib/intlShape';
import { getRoutePath } from '@routes/routes.util';
import { withRouter } from 'next/router';
import { withActiveLocale, withActiveLocalePropType } from '../../../hocs/withActiveLocale';
import withAnalytics, { withAnalyticsPropType } from '../../../hocs/withAnalytics';
import withAuth, { withAuthPropType } from '../../../hocs/withAuth';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';
import {
  PRIMARY_NAV_LINKS,
  HELP_SUPPORT_LINK,
  JOIN_LINK,
  LOGO_LINK,
  MAIN_LINKS_AUTHENTICATED,
  MOCK_NOTIFICATIONS
} from './config';
import messages from './messages';

/**
 * The `MainNavContainer` is responsible for sourcing everything that needs to be displayed
 * in the navigation and handling it's authenticated user functionality.
 *
 * @class MainNavContainer
 * @extends {Component}
 */
class MainNavContainer extends Component {
  constructor(props) {
    super(props);
    const { type, router } = this.props;
    const { publicRuntimeConfig } = getConfig();
    const { PORTAL = 'admin' } = publicRuntimeConfig || {};
    this.router = router;
    this.isSupplierPortal = PORTAL === 'supplier' || type === 'supplier';

    // Lots of the "things" being passed to the nav will never change after the component is first mounted.
    // To keep things performant (not running on every render call) these "things" are set up in the constructor.
    this.joinLink = this.buildLink(JOIN_LINK);
    this.logo = this.buildLink(LOGO_LINK);
    this.helpSupport = this.buildLink(HELP_SUPPORT_LINK);

    this.subMenuLinks = this.getSubMenuLinks(PRIMARY_NAV_LINKS);
    this.userMenuLinks = this.isSupplierPortal ? [] : this.buildLinks(MAIN_LINKS_AUTHENTICATED);
    this.notifications = this.buildLinks(MOCK_NOTIFICATIONS, true);

    this.signOut = this.buildSignOut();

    // Binding event handlers
    this.contactClick = this.contactClick.bind(this);
    this.organizationChange = this.organizationChange.bind(this);
  }

  componentDidMount() {
    this.prefetchRoutes();
  }

  /**
   * Get a list of organizations the user belongs to.
   *
   * TODO: This is very untested and will need work once the API starts to return
   * users with multiple organizations or roles.
   *
   * @returns {Object[]} Array of objects containing organization id and names.
   * @memberof MainNavContainer
   */
  getOrganizations() {
    const { auth } = this.props;

    const organizationRoles = get(auth, 'me.organizations', []);

    // Mock data because organizations are not returned on every user
    if (organizationRoles.length === 0) {
      return [
        {
          id: 0,
          name: 'A Mock Organization'
        }
      ];
    }

    // The list of organizationRoles contains duplicate organizations and needs to be filtered down
    const organizationsUnique = organizationRoles.reduce((organizations, organizationRole) => {
      // Using "orgId" to detect uniqueness
      const id = get(organizationRole, 'id');
      const isStub = get(organizationRole, 'stub', false);
      // Stub organizations are filtered out and never show to the user
      if (isStub) {
        return organizations;
      }
      // Checking to see if the organization is already in array of unique organizations
      const isUnique = organizations.find((organization) => organization.id === id) === undefined;

      // Organization is not yet in the array
      if (isUnique) {
        // Add an organization to the array
        // Object shape is what is required by the navigation's organization selector
        organizations.push({
          id,
          name: get(organizationRole, 'name'),
          role: get(organizationRole, 'role.title'),
          permissions: get(organizationRole, 'role.permissions')
        });
      }

      return organizations;
    }, []);

    return organizationsUnique;
  }

  /**
   * Generates a list of nav items for display in the sub/side menu (for authenticated users).
   *
   * The nav items displayed here may change based on a number of external factors:
   * - The user's permissions?
   *
   * @returns {Array} Array of link objects.
   * @memberof MainNavContainer
   */
  getSubMenuLinks(links) {
    const subMenuLinks = links.map((menu) => {
      let subMenu = [];
      if (menu.subMenu.length !== 0) {
        subMenu = this.buildLinks(menu.subMenu);
      }
      const mainLink = this.buildLink(menu);
      return {
        ...mainLink,
        subMenu
      };
    });

    return subMenuLinks;
  }

  /**
   * This function is called when a user clicks on the phone icon
   * in the mobile nav.
   *
   * @memberof MainNavContainer
   */
  contactClick() {
    const { analytics, intl } = this.props;

    analytics.trackEvent({
      event: 'global-nav-click',
      clickID: 'contact',
      phoneNumber: intl.formatMessage(messages.linkContact)
    });
  }

  /**
   * Because the Link component is not being used we need to implement our own prefetching.
   *
   * This function prefetches some of the routes the user is likely to hit next.
   *
   * @returns {Promise} Resolves when all routes have been prefetched.
   * @memberof MainNavContainer
   */
  prefetchRoutes() {
    // Build a list of links to prefetch
    const hrefToPrefetch = [
      this.joinLink.href,
      this.logo.href,
      this.signOut.href,
      ...this.userMenuLinks.map((link) => link.href)
    ];

    // Prefetch all of the links
    return Promise.all(hrefToPrefetch.map((href) => this.router.prefetch(href)));
  }

  /**
   * Builds a link object from the passed in configuration object.
   *
   * @param {Object} linkConfig Link configuration object.
   * @returns {Object} Link object.
   * @memberof MainNavContainer
   */
  buildLink(linkConfig, noformat = false) {
    const { activeLocale, analytics, intl } = this.props;

    // Localize link label
    const label = noformat ? linkConfig.label : intl.formatMessage(linkConfig.label);

    // Generate link href
    const href = getRoutePath({ routeName: linkConfig.route, locale: activeLocale.id, params: linkConfig.params });

    // Generate a unique id for the link
    const id = `${href}|${label}`;

    // Generate a click handler for the link
    const onClick = (event, routeChanged) => {
      // Prevent a hard page reload when link is clicked
      event.preventDefault();

      // Route to the the page
      this.router.push(href).then(() => {
        window.scrollTo(0, 0);
        document.body.focus();

        // Closes menu after navigating to the new route
        if (typeof routeChanged === 'function') {
          routeChanged();
        }
      });

      // Track click of nav item
      analytics.trackEvent({
        event: 'global-nav-click',
        clickID: linkConfig.analyticID,
        pagePath: href
      });
    };

    return {
      routeMatches: linkConfig.routeMatches,
      icon: linkConfig.icon,
      subMenu: linkConfig.subMenu ? linkConfig.subMenu : [],
      id,
      href,
      label,
      onClick,
      reqPermission: linkConfig.reqPermission,
      route: linkConfig.route,
      secondaryNav: linkConfig.secondaryNav
    };
  }

  /**
   * Build an array of link objects from an array of link configuration objects.
   *
   * @param {Object[]} linkConfigs Array of link configuration objects.
   * @returns {Object[]} Array of link objects.
   * @memberof MainNavContainer
   */
  buildLinks(linkConfigs, noformat = false) {
    return linkConfigs.map((linkConfig) => this.buildLink(linkConfig, noformat));
  }

  /**
   * Build a link for the Sign Out button.
   *
   * @returns {Object}
   * @memberof MainNavContainer
   */
  buildSignOut() {
    const { analytics, auth, intl } = this.props;

    // Localize link label
    const label = intl.formatMessage(messages.linkSignOut);

    // Generate link href
    const href = '#';

    // Generate a unique id for the link
    const id = `${href}|${label}`;

    // Generate a click handler for sign out
    const onClick = (event) => {
      event.preventDefault();

      // Signs out the user using HOC
      auth.logout();

      // Track click of sign out
      analytics.trackEvent({
        event: 'global-nav-click',
        clickID: 'sign out',
        pagePath: href
      });
    };

    return {
      id,
      href,
      label,
      onClick
    };
  }

  /**
   * This function is called when the user selects an organization from their
   * list of organizations.
   *
   * @param {Object} organization Organization object containing id and name.
   * @memberof MainNavContainer
   */
  organizationChange(organization) {
    const { analytics, auth } = this.props;

    // Call mutation to change organization
    // There is nothing we can really do on the frontend if this fails so there is no error handling
    auth.changeOrganization({
      organizationId: organization.id
    });

    // Track changing of organization
    analytics.trackEvent({
      event: 'global-nav-click',
      clickID: 'organization-change',
      organizationName: organization.name,
      organizationId: organization.id
    });
  }

  /**
   * Generates the mainNav object that is passed out of this container.
   *
   * @returns {Object}
   * @memberof MainNavContainer
   */
  buildMainNav() {
    const { auth, intl } = this.props;

    return {
      authenticated: get(auth, 'authenticated', false),
      contactNumber: intl.formatMessage(messages.linkContact),
      onContactClick: this.contactClick,
      joinLink: this.isSupplierPortal ? this.joinLink : null,
      logo: this.logo,
      helpSupport: this.helpSupport,
      notifications: this.notifications,
      signOut: this.signOut,
      subMenuLinks: this.subMenuLinks,
      userMenuLinks: this.userMenuLinks,
      profilePicture: get(this.props, 'me.profilePicture'),
      organizationChange: this.organizationChange,
      organizations: this.getOrganizations(),
      userCurrentOrganization: get(auth, 'me.activeOrganization.id'),
      userFirstName: get(auth, 'me.firstName'),
      userLastName: get(auth, 'me.lastName')
    };
  }

  render() {
    const { children } = this.props;
    const renderProps = {
      mainNav: this.buildMainNav()
    };
    return children(renderProps);
  }
}
MainNavContainer.propTypes = {
  children: ReactChildrenPropType.isRequired,
  type: PropTypes.oneOf(['admin', 'supplier']).isRequired,
  activeLocale: withActiveLocalePropType.isRequired,
  analytics: withAnalyticsPropType.isRequired,
  auth: withAuthPropType.isRequired,
  intl: intlShape.isRequired,
  router: PropTypes.shape({
    prefetch: PropTypes.func.isRequired
  }).isRequired
};

/**
 * Providing this component with Intl localizations as well as
 * data and mutations from GraphQL.
 */
const ComposedMainNavContainer = compose(
  withActiveLocale,
  injectIntl,
  withAuth(),
  withAnalytics(),
  withRouter
)(MainNavContainer);
export default ComposedMainNavContainer;
